import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { eventDateFormat } from "../../../helpers/helpers";
import {
  clearPendingSubmission,
  createPropertyExport,
  fetchLegacyExportPlan,
  fetchPropertyExportDownload,
  idempotencyKeyFor,
  listPropertyExports,
} from "../../../helpers/propertyExports";

const PAGE_SIZE = 5;
const REFRESH_MS = 4000;
const ACTIVE_STATUSES = new Set(["queued", "processing"]);

function startDownload(url) {
  const link = document.createElement("a");
  link.href = url;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function statusLabel(status) {
  // Legacy rows predate the status column, so this is reached with null.
  if (!status) return "Ready";

  return String(status)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const Exports = () => {
  const navigate = useNavigate();
  const legacyExportState = useSelector(
    (state) => state.fetchProfileSettings.exportsData
  );
  const legacyExports = Array.isArray(legacyExportState) ? legacyExportState : [];
  const [exportsData, setExportsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const load = async () => {
      try {
        const result = await listPropertyExports(currentPage + 1, PAGE_SIZE);
        if (cancelled) return;
        setExportsData(result.data || []);
        setTotalPages(result.last_page || 0);
        setLoadError(false);
        setIsLoading(false);

        if ((result.data || []).some((item) => ACTIVE_STATUSES.has(item.status))) {
          timer = setTimeout(load, REFRESH_MS);
        }
      } catch (error) {
        // Silently showing an empty table reads as "you have no exports",
        // which is a different and much more alarming thing to tell someone.
        if (cancelled) return;
        setLoadError(true);
        setIsLoading(false);
        timer = setTimeout(load, REFRESH_MS);
      }
    };

    load();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [currentPage]);

  const downloadArtifact = async (row) => {
    setDownloadingId(row.id);
    try {
      const download = await fetchPropertyExportDownload(row.id);
      if (!download?.url) {
        // An anchor with an empty href navigates to the current page, so the
        // failure would look like the app randomly reloading itself.
        throw new Error("The download link was missing from the response.");
      }
      startDownload(download.url);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "That export could not be downloaded."
      );
    } finally {
      setDownloadingId(null);
    }
  };

  /**
   * Rebuild a legacy export through the current pipeline.
   *
   * The old regeneration re-ran the synchronous export, which stops after 25
   * fresh contacts and a time budget, so a 200-property file came back with 25
   * contacts at best - and it was assembled in the browser. This submits the
   * saved selection as an ordinary export instead: contacts the account already
   * owns are reused for free, and only the ones it never got are bought.
   */
  const regenerateLegacyExport = async (row) => {
    setDownloadingId(`legacy-${row.id}`);
    try {
      const { unsupported_columns: unsupported = [], ...payload } =
        await fetchLegacyExportPlan(row.id);

      // Asked before anything is queued or spent. Warning afterwards told the
      // user what their file would be missing once it was too late to decline.
      if (unsupported.length > 0 && !window.confirm(
        `These columns are not available in the new export format and will be missing: ${unsupported.join(", ")}.\n\nRegenerate anyway?`
      )) {
        return;
      }

      const created = await createPropertyExport(payload, idempotencyKeyFor(payload));
      clearPendingSubmission(payload);
      localStorage.setItem("activePropertyExportId", created.id);

      navigate("/export");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "That export could not be regenerated."
      );
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="setting_blk cell_min_padding">
      <h4>Exports</h4>
      <div className="table_blk">
        <div className="inner_tbl relative">
          {isLoading && <div className="loadingTable">Loading exports…</div>}
          <table className="cell_hide_dowload_lbl">
            <thead><tr><th>Date</th><th>Name</th><th>Status</th><th /></tr></thead>
            <tbody>
              {exportsData.map((row) => {
                const downloadable = ["completed", "completed_with_warnings"].includes(row.status);
                const extension = row.file_type === "excel" ? "xlsx" : "csv";
                return (
                  <tr key={row.id}>
                    <td>{eventDateFormat(row.created_at)}</td>
                    <td>{row.name}.{extension}</td>
                    <td>
                      {statusLabel(row.status)}
                      {ACTIVE_STATUSES.has(row.status) && ` (${row.processed}/${row.total})`}
                    </td>
                    <td>
                      {downloadable && (
                        <button
                          type="button"
                          className="down_load_lbl"
                          aria-label={`Download ${row.name}`}
                          disabled={downloadingId === row.id}
                          onClick={() => downloadArtifact(row)}
                        >
                          <span>Download</span><img src="/images/download_icon.svg" alt="" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {currentPage === 0 && legacyExports.map((row) => (
                <tr key={`legacy-${row.id}`}>
                  <td>{eventDateFormat(row.created_date)}</td>
                  <td>{row.file_name}.csv (legacy)</td>
                  <td>Legacy</td>
                  <td>
                    <button
                      type="button"
                      className="down_load_lbl"
                      aria-label={`Regenerate ${row.file_name}`}
                      disabled={downloadingId === `legacy-${row.id}`}
                      onClick={() => regenerateLegacyExport(row)}
                    >
                      <span>Regenerate</span><img src="/images/download_icon.svg" alt="" />
                    </button>
                  </td>
                </tr>
              ))}
              {!isLoading && loadError && (
                <tr><td colSpan="4">Your exports could not be loaded. Please try again.</td></tr>
              )}
              {!isLoading && !loadError && exportsData.length === 0
                && (currentPage > 0 || legacyExports.length === 0) && (
                <tr><td colSpan="4">The properties you export will show here.</td></tr>
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="pagination_wrap">
              <ReactPaginate
                previousLabel="Prev"
                nextLabel="Next"
                breakLabel="..."
                pageCount={totalPages}
                marginPagesDisplayed={1}
                pageRangeDisplayed={3}
                onPageChange={({ selected }) => setCurrentPage(selected)}
                containerClassName="pagination"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                activeClassName="active"
                forcePage={currentPage}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Exports;
